import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth-provider';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: () => void;
          cancel: () => void;
        };
      };
    };
  }
}

interface GoogleOAuthButtonProps {
  onError?: (error: string) => void;
  redirectPath?: string | null;
}

export function GoogleOAuthButton({ onError, redirectPath }: GoogleOAuthButtonProps) {
  const { t } = useTranslation('auth');
  const { setToken } = useAuth();
  const navigate = useNavigate();
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const handleGoogleResponse = async (response: any) => {
    try {
      if (!response.credential) {
        throw new Error(t('google.errors.noCredential'));
      }

      const result = await fetch(`${import.meta.env.VITE_API_URL}/v1/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: response.credential,
        }),
      });

      const data = await result.json();

      if (data.accessToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
        setToken(data.accessToken);
        if (redirectPath) {
          navigate(decodeURIComponent(redirectPath));
        } else {
          navigate('/');
        }
      } else {
        throw new Error(t('google.errors.noAccessToken'));
      }
    } catch (error) {
      console.error('Google login error:', error);
      onError?.(error instanceof Error ? error.message : t('google.errors.loginFailed'));
    }
  };

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

    const initializeGoogleAuth = () => {
      if (!window.google?.accounts) {
        setTimeout(initializeGoogleAuth, 100);
        return;
      }

      if (isInitialized) return;

      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        if (googleButtonRef.current) {
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            width: googleButtonRef.current.offsetWidth || 400,
            logo_alignment: 'center',
          });
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing Google OAuth:', error);
        onError?.(t('google.errors.initFailed'));
      }
    };

    initializeGoogleAuth();

    return () => {
      if (isInitialized) {
        try {
          window.google?.accounts?.id?.cancel();
        } catch (error) {
          console.error('Error cleaning up Google OAuth:', error);
        }
      }
    };
  }, [isInitialized, onError, navigate, redirectPath, setToken, t]);

  return (
    <div className="flex flex-col gap-4">
      <div ref={googleButtonRef} className="w-full !flex !justify-center"></div>
    </div>
  );
}
